import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTimer } from "react-timer-hook";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import OtpInput from "react-otp-input";
import ReCAPTCHA from "react-google-recaptcha-enterprise";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useLogin } from "../../store/backend/useLogin";
import {
  useGenerateOtp,
  useLoginWithOtp,
  useChangePassword,
} from "store/backend/assets";
import useAuth from "../../hooks/useAuth";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import {
  ErrorMessage,
  Heading1,
  PhoneWithIsdField,
  PrimaryButton,
} from "widgets";
import Loader from "../../Shared/Loader/Loader";
import PasswordField from "widgets/PasswordField/PasswordField";
import { removeToken } from "utils/tokenUtils";
import { SITE_KEY } from "constants/config";
import { useCountryIsdList } from "services/mdsService";
import { getPhoneValidationError } from "utils/phoneValidation";
import { storeUserVerticals } from "utils/userVerticals";

import styles from "./Login.module.scss";

const AUTH_FLOW = {
  LOGIN: "LOGIN",
  FORGOT_PASSWORD: "FORGOT_PASSWORD",
};

const FORGOT_PASSWORD_STEPS = ["PHONE", "OTP", "CHANGE_PASSWORD"];
const [FORGOT_PASSWORD_PHONE, FORGOT_PASSWORD_OTP, FORGOT_PASSWORD_CHANGE_PASSWORD] =
  FORGOT_PASSWORD_STEPS;

const getNextForgotPasswordStep = (step) => {
  const index = FORGOT_PASSWORD_STEPS.indexOf(step);
  return FORGOT_PASSWORD_STEPS[index + 1] ?? step;
};

const getPreviousForgotPasswordStep = (step) => {
  const index = FORGOT_PASSWORD_STEPS.indexOf(step);
  return FORGOT_PASSWORD_STEPS[index - 1] ?? step;
};

const LOGIN_INITIAL_VALUES = {
  isd_code: "",
  mobile: "",
  password: "",
};

const FORGOT_PASSWORD_INITIAL_VALUES = {
  isd_code: "",
  mobile: "",
  otp: "",
  newPassword: "",
  confirmPassword: "",
  step: FORGOT_PASSWORD_PHONE,
};

const PASSWORD_RULES = [
  {
    label: "At least 6 characters",
    isValid: (password) => password.length >= 6,
  },
  {
    label: "No more than 64 characters",
    isValid: (password) => password.length > 0 && password.length <= 64,
  },
  {
    label: "No spaces",
    isValid: (password) => Boolean(password) && /^\S+$/.test(password),
  },
  {
    label: "No consecutive dots (..)",
    isValid: (password) => Boolean(password) && !password.includes(".."),
  },
  {
    label: "No consecutive underscores (__)",
    isValid: (password) => Boolean(password) && !password.includes("__"),
  },
];

const createPhoneValidationTest = (requiredMessage) =>
  function validatePhone(value) {
    if (!value) {
      return this.createError({ message: requiredMessage });
    }

    const error = getPhoneValidationError(value, this.parent.isd_code);
    if (error) {
      return this.createError({ message: error });
    }

    return true;
  };

const loginValidationSchema = Yup.object({
  mobile: Yup.string().test(
    "phone-validation",
    createPhoneValidationTest("Mobile number is required")
  ),
  password: Yup.string()
    .required("Password is required")
    .min(6, "At least 6 characters"),
});

const forgotPasswordValidationSchema = Yup.object({
  mobile: Yup.string().when("step", {
    is: FORGOT_PASSWORD_PHONE,
    then: (schema) =>
      schema.test(
        "phone-validation",
        createPhoneValidationTest("Mobile number is required")
      ),
  }),
  newPassword: Yup.string().when("step", {
    is: FORGOT_PASSWORD_CHANGE_PASSWORD,
    then: (schema) =>
      schema
        .required("New password is required")
        .min(6, "Minimum 6 characters")
        .max(64, "Maximum 64 characters")
        .test(
          "no-spaces",
          "Password must not contain spaces",
          (value) => value && !/\s/.test(value)
        )
        .test(
          "no-consecutive-dots",
          "Password must not contain consecutive dots (..)",
          (value) => value && !value.includes("..")
        )
        .test(
          "no-consecutive-underscores",
          "Password must not contain consecutive underscores (__)",
          (value) => value && !value.includes("__")
        )
        .trim(),
  }),
  confirmPassword: Yup.string().when("step", {
    is: FORGOT_PASSWORD_CHANGE_PASSWORD,
    then: (schema) =>
      schema
        .required("Please confirm your password")
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
  }),
});

const handleOnSuccessLogin = (data, authLogin, navigate, mobile) => {
  authLogin(data.access_token);
  localStorage.setItem("sales_person_id", data.crn_id);
  localStorage.setItem("roles", data.roles);
  localStorage.setItem("phoneNumber", mobile);
  localStorage.setItem("sales_person_uam_id", data.uam_id);
  storeUserVerticals(data.verticals);
  navigate("/homePage");
};

const handleLoginFormSubmit = (values, { login, authLogin, navigate, openSnackbar }) => {
  login(values, {
    onSuccess: (data) =>
      handleOnSuccessLogin(data, authLogin, navigate, values.mobile),
    onError: (err) =>
      openSnackbar(err?.message || "Unable to log in", "error"),
  });
};

const generateOtpRequest = async ({
  phone,
  isd_code,
  recaptchaRef,
  generateOtp,
  openSnackbar,
  setFieldValue,
  restartTimer,
  clearOtp = false,
}) => {
  if (clearOtp) {
    setFieldValue("otp", "");
  }

  const phoneError = getPhoneValidationError(phone, isd_code);
  if (phoneError) {
    openSnackbar(phoneError, "error");
    return;
  }

  try {
    const captchaToken = await recaptchaRef.current.executeAsync("generate_otp");
    recaptchaRef.current.reset();

    if (!captchaToken) {
      openSnackbar(
        "Multiple OTP requests detected. Please try again after 2 minutes.",
        "error"
      );
      return;
    }

    generateOtp(
      { phone, isd_code, captcha_token: captchaToken },
      {
        onSuccess: () => {
          openSnackbar("OTP sent successfully!", "success");
          setFieldValue("step", getNextForgotPasswordStep(FORGOT_PASSWORD_PHONE));
          const newExpiry = new Date();
          newExpiry.setSeconds(newExpiry.getSeconds() + 60);
          restartTimer(newExpiry);
        },
        onError: (error) => {
          openSnackbar(error?.message || "Error sending OTP", "error");
        },
      }
    );
  } catch (err) {
    openSnackbar(err?.message || "Failed to get captcha token", "error");
  }
};

const submitForgotPasswordPhoneStep = ({
  values,
  setFieldValue,
  recaptchaRef,
  generateOtp,
  openSnackbar,
  restartTimer,
}) => {
  generateOtpRequest({
    phone: values.mobile,
    isd_code: values.isd_code,
    recaptchaRef,
    generateOtp,
    openSnackbar,
    setFieldValue,
    restartTimer,
    clearOtp: Boolean(values.otp && values.otp.length === 4),
  });
};

const submitForgotPasswordOtpStep = ({
  values,
  setFieldValue,
  loginWithOtp,
  authLogin,
  openSnackbar,
}) => {
  loginWithOtp(
    {
      phone: values.mobile,
      otp: values.otp,
      isd_code: values.isd_code,
    },
    {
      onSuccess: (data) => {
        authLogin(data.access_token);
        openSnackbar("OTP verified successfully!", "success");
        setFieldValue("step", getNextForgotPasswordStep(values.step));
      },
      onError: (err) => openSnackbar(err?.message || "Invalid OTP", "error"),
    }
  );
};

const submitForgotPasswordChangePasswordStep = ({
  values,
  resetForm,
  changePassword,
  openSnackbar,
  onBackToLogin,
}) => {
  changePassword(
    { new_password: values.newPassword },
    {
      onSuccess: () => {
        openSnackbar(
          "Password changed successfully! Please log in.",
          "success"
        );
        removeToken();
        resetForm();
        onBackToLogin();
      },
      onError: (err) =>
        openSnackbar(err?.message || "Failed to change password", "error"),
    }
  );
};

const handleForgotPasswordFormSubmit = (values, helpers, dependencies) => {
  if (values.step === FORGOT_PASSWORD_PHONE) {
    submitForgotPasswordPhoneStep({
      values,
      setFieldValue: helpers.setFieldValue,
      ...dependencies,
    });
    return;
  }

  if (values.step === FORGOT_PASSWORD_OTP) {
    submitForgotPasswordOtpStep({
      values,
      setFieldValue: helpers.setFieldValue,
      ...dependencies,
    });
    return;
  }

  if (values.step === FORGOT_PASSWORD_CHANGE_PASSWORD) {
    submitForgotPasswordChangePasswordStep({
      values,
      resetForm: helpers.resetForm,
      ...dependencies,
    });
  }
};

const PasswordRulesList = ({ password }) => (
  <ul className={styles.passwordRules}>
    {PASSWORD_RULES.map((rule) => {
      const isValid = rule.isValid(password);

      return (
        <li
          key={rule.label}
          className={`${styles.passwordRule} ${
            isValid ? styles.valid : styles.invalid
          }`}
        >
          <span>{isValid ? "✔" : "✖"}</span>
          {rule.label}
        </li>
      );
    })}
  </ul>
);

const LoginFlow = ({ onChangePassword }) => {
  const { openSnackbar } = useSnackbarContext();
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const {
    mutate: login,
    isPending: isLoginLoading,
    isError,
    error,
  } = useLogin();

  const loginSubmitDependencies = {
    login,
    authLogin,
    navigate,
    openSnackbar,
  };

  return (
    <Formik
      initialValues={LOGIN_INITIAL_VALUES}
      validationSchema={loginValidationSchema}
      onSubmit={(values) =>
        handleLoginFormSubmit(values, loginSubmitDependencies)
      }
    >
      {({ values, setFieldValue, setFieldTouched, handleSubmit, errors, touched }) => (
        <Form onSubmit={handleSubmit} autoComplete="off">
          <div className={styles.loginContainer}>
            <Heading1 heading="Login" />
            <PhoneWithIsdField
              id="mobile"
              label="Mobile"
              isdCode={values.isd_code}
              phone={values.mobile}
              onIsdCodeChange={(nextIsdCode) =>
                setFieldValue("isd_code", nextIsdCode)
              }
              onPhoneChange={(nextPhone) => setFieldValue("mobile", nextPhone)}
              onPhoneBlur={() => setFieldTouched("mobile", true)}
              error={touched.mobile && errors.mobile}
            />
            <PasswordField
              id="password"
              label="Password"
              value={values.password}
              onChange={(event) => setFieldValue("password", event.target.value)}
              error={errors.password}
            />
            {isError && (
              <ErrorMessage>
                {error?.message || "Unable to log in"}
              </ErrorMessage>
            )}
            <div
              className={styles.changePasswordLink}
              onClick={onChangePassword}
            >
              Change Password
            </div>
            <div className={styles.btnContainer}>
              <PrimaryButton type="submit" disabled={isLoginLoading}>
                {isLoginLoading ? <Loader /> : "Login"}
              </PrimaryButton>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const ForgotPasswordPhoneStep = ({
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  isOtpLoading,
  onBack,
}) => {
  const isPhoneInvalid = getPhoneValidationError(
    values.mobile,
    values.isd_code
  );

  return (
    <div className={styles.loginContainer}>
      <ArrowBackIosIcon className={styles.goBackIcon} onClick={onBack} />
      <Heading1 heading="Please enter your phone number" />
      <PhoneWithIsdField
        id="mobile"
        label="Enter Mobile Number"
        isdCode={values.isd_code}
        phone={values.mobile}
        onIsdCodeChange={(nextIsdCode) =>
          setFieldValue("isd_code", nextIsdCode)
        }
        onPhoneChange={(nextPhone) => setFieldValue("mobile", nextPhone)}
        onPhoneBlur={() => setFieldTouched("mobile", true)}
        error={touched.mobile && errors.mobile}
      />
      <PrimaryButton
        type="submit"
        disabled={!values.mobile || Boolean(isPhoneInvalid) || isOtpLoading}
      >
        {isOtpLoading ? <Loader /> : "Request OTP"}
      </PrimaryButton>
    </div>
  );
};

const ForgotPasswordOtpStep = ({
  values,
  setFieldValue,
  seconds,
  isLoggingIn,
  onResendOtp,
  onBack,
}) => (
  <div className={styles.loginContainer}>
    <ArrowBackIosIcon className={styles.goBackIcon} onClick={onBack} />
    <div className={styles.heading}>OTP Verification</div>
    <div className={styles.subHeading}>
      We have sent an OTP to{" "}
      <b>
        {values.isd_code} {values.mobile}
      </b>
    </div>
    <div className={styles.fieldWrapper}>
      <OtpInput
        value={values.otp}
        onChange={(value) => setFieldValue("otp", value)}
        numInputs={4}
        isInputNum
        renderSeparator={<span>-</span>}
        renderInput={(props) => (
          <input {...props} className={styles.otpInput} />
        )}
        containerClassName={styles.otpContainer}
      />
    </div>
    <div className={styles.resendWrapper}>
      {seconds > 0 && (
        <div className={styles.resendOtpText}>Resend OTP in {seconds}s</div>
      )}
      {seconds <= 0 && (
        <div onClick={onResendOtp} className={styles.resendOtpLink}>
          Resend OTP
        </div>
      )}
    </div>
    <PrimaryButton type="submit" disabled={values.otp.length < 4}>
      {isLoggingIn ? <Loader /> : "Verify OTP"}
    </PrimaryButton>
  </div>
);

const ForgotPasswordChangeStep = ({
  values,
  errors,
  setFieldValue,
  isChangePasswordLoading,
  onBack,
}) => (
  <div className={styles.loginContainer}>
    <ArrowBackIosIcon className={styles.goBackIcon} onClick={onBack} />
    <div className={styles.heading}>Change Password</div>
    <PasswordField
      id="newPassword"
      label="New Password"
      value={values.newPassword}
      onChange={(event) => setFieldValue("newPassword", event.target.value)}
      error={errors.newPassword}
    />
    <PasswordField
      id="confirmPassword"
      label="Confirm Password"
      value={values.confirmPassword}
      onChange={(event) => setFieldValue("confirmPassword", event.target.value)}
      error={errors.confirmPassword}
    />
    <PasswordRulesList password={values.newPassword} />
    <PrimaryButton
      type="submit"
      disabled={
        !values.newPassword ||
        !values.confirmPassword ||
        errors.newPassword ||
        errors.confirmPassword
      }
    >
      {isChangePasswordLoading ? <Loader /> : "Change Password"}
    </PrimaryButton>
  </div>
);

const ForgotPasswordFlow = ({ onBackToLogin }) => {
  const { openSnackbar } = useSnackbarContext();
  const { login: authLogin } = useAuth();
  const recaptchaRef = useRef(null);

  const { mutate: generateOtp, isPending: isOtpLoading } = useGenerateOtp();
  const { mutate: loginWithOtp, isPending: isLoggingIn } = useLoginWithOtp();
  const { mutate: changePassword, isPending: isChangePasswordLoading } =
    useChangePassword();

  const { seconds, restart } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
  });

  const forgotPasswordSubmitDependencies = {
    recaptchaRef,
    generateOtp,
    loginWithOtp,
    changePassword,
    authLogin,
    openSnackbar,
    restartTimer: restart,
    onBackToLogin,
  };

  return (
    <Formik
      initialValues={FORGOT_PASSWORD_INITIAL_VALUES}
      validationSchema={forgotPasswordValidationSchema}
      onSubmit={(values, helpers) =>
        handleForgotPasswordFormSubmit(
          values,
          helpers,
          forgotPasswordSubmitDependencies
        )
      }
    >
      {({ values, setFieldValue, setFieldTouched, handleSubmit, errors, touched }) => (
        <Form onSubmit={handleSubmit} autoComplete="off">
          {(values.step === FORGOT_PASSWORD_PHONE ||
            values.step === FORGOT_PASSWORD_OTP) && (
            <ReCAPTCHA sitekey={SITE_KEY} size="invisible" ref={recaptchaRef} />
          )}

          {values.step === FORGOT_PASSWORD_PHONE && (
            <ForgotPasswordPhoneStep
              values={values}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              isOtpLoading={isOtpLoading}
              onBack={onBackToLogin}
            />
          )}

          {values.step === FORGOT_PASSWORD_OTP && (
            <ForgotPasswordOtpStep
              values={values}
              setFieldValue={setFieldValue}
              seconds={seconds}
              isLoggingIn={isLoggingIn}
              onResendOtp={() =>
                generateOtpRequest({
                  phone: values.mobile,
                  isd_code: values.isd_code,
                  recaptchaRef,
                  generateOtp,
                  openSnackbar,
                  setFieldValue,
                  restartTimer: restart,
                })
              }
              onBack={() =>
                setFieldValue("step", getPreviousForgotPasswordStep(values.step))
              }
            />
          )}

          {values.step === FORGOT_PASSWORD_CHANGE_PASSWORD && (
            <ForgotPasswordChangeStep
              values={values}
              errors={errors}
              setFieldValue={setFieldValue}
              isChangePasswordLoading={isChangePasswordLoading}
              onBack={() =>
                setFieldValue("step", getPreviousForgotPasswordStep(values.step))
              }
            />
          )}
        </Form>
      )}
    </Formik>
  );
};

const Login = () => {
  const [flow, setFlow] = useState(AUTH_FLOW.LOGIN);
  const {
    isPending: isCountryIsdListPending,
    isFetching: isCountryIsdListFetching,
  } = useCountryIsdList();
  const isWaitingForCountryIsdList =
    isCountryIsdListPending && isCountryIsdListFetching;

  if (isWaitingForCountryIsdList) {
    return (
      <div className={`${styles.loginContainer} ${styles.loginLoader}`}>
        <Loader />
      </div>
    );
  }

  if (flow === AUTH_FLOW.LOGIN) {
    return (
      <LoginFlow
        onChangePassword={() => setFlow(AUTH_FLOW.FORGOT_PASSWORD)}
      />
    );
  }

  if (flow === AUTH_FLOW.FORGOT_PASSWORD) {
    return (
      <ForgotPasswordFlow onBackToLogin={() => setFlow(AUTH_FLOW.LOGIN)} />
    );
  }

  return null;
};

export default Login;
